import type { CSSProperties } from 'react';

import { cn } from '../cn.js';
import { addons, addonsKeys } from './defs/addons.js';
import { aliases, aliasesKeys } from './defs/aliases.js';
import { overwrites, overwritesKeys } from './defs/overwrites.js';
import { StandardCSSProperties, SxProps, SystemStyleObject } from './types.js';
import { virtualTheme } from './virtualTheme.js';

/**
 * Set of CSS pseudo-selectors and nested selectors that are NOT supported.
 * These require CSS-in-JS or separate stylesheet handling.
 */
const UNSUPPORTED_SELECTOR_PREFIXES = [':', '&', '@'] as const;

/**
 * Warns about unsupported sx features in development mode only.
 */
function warnUnsupportedFeature(feature: string, details?: string): void {
  if (import.meta.env.DEV) {
    const detailsSuffix = details ? `. ${details}` : '';
    console.warn(
      `[processSxProps] Unsupported feature: ${feature}${detailsSuffix}`,
    );
  }
}

/**
 * Checks if a key looks like a pseudo-selector or nested selector.
 */
function isNestedOrPseudoSelector(key: string): boolean {
  return UNSUPPORTED_SELECTOR_PREFIXES.some((prefix) => key.startsWith(prefix));
}

export type SxIncoming = {
  sx?: SxProps<any>;
  className?: string;
  style?: CSSProperties;
};

/**
 * Resolves a single sx entry to its className/style contribution, applying the
 * theme callback (if any) and routing through aliases/overwrites/addons.
 * Returns `null` when the entry produces no value and should be skipped.
 */
function resolveSxValue(
  key: string,
  data: NonNullable<SystemStyleObject<typeof virtualTheme>>,
): { className?: string; style?: StandardCSSProperties } | null {
  let item = data[key as keyof typeof data];
  if (typeof item === 'function') {
    if (import.meta.env.DEV) {
      console.warn(
        `Sx argument uses callback as a prop(${key}) value syntax, which is not supported by latest MUI. Please wrap sx into callback instead.`,
      );
    }
    // @ts-expect-error We are sure that item is a function
    item = item(virtualTheme);
  }
  if (!item) {
    return null;
  }
  if (aliasesKeys.includes(key as keyof typeof aliases)) {
    return aliases[key as keyof typeof aliases](item);
  }
  if (overwritesKeys.includes(key as keyof typeof overwrites)) {
    return overwrites[key as keyof typeof overwrites](item);
  }
  if (addonsKeys.includes(key as keyof typeof addons)) {
    return addons[key as keyof typeof addons](item);
  }
  return { style: item };
}

function applySx(data: SystemStyleObject<typeof virtualTheme>) {
  if (!data) {
    return {
      style: {},
      className: '',
    };
  }
  const result: {
    style: CSSProperties;
    className: string;
  } = {
    style: {},
    className: '',
  };
  const sxKeys = Object.keys(data);
  for (const key of sxKeys) {
    // Check for unsupported pseudo-selectors and nested selectors
    if (isNestedOrPseudoSelector(key)) {
      warnUnsupportedFeature(
        `pseudo/nested selector "${key}"`,
        'Pseudo-selectors like :hover, :focus and nested selectors like "& > div" require CSS-in-JS. Use className with Tailwind variants instead (e.g., hover:bg-blue-500)',
      );
      continue;
    }

    const res = resolveSxValue(key, data);
    if (!res) {
      continue;
    }
    if (res.className) {
      result.className = `${result.className} ${res.className}`;
    }
    if (res.style) {
      result.style = { ...result.style, ...(res.style as CSSProperties) };
    }
  }
  return result;
}

/**
 * Processes MUI-style `sx` prop and converts it to className + style for use with Tailwind CSS.
 *
 * This is a compatibility utility for migrating from MUI Box to native elements with Tailwind.
 *
 * ## Supported Features
 *
 * ### Spacing Aliases (uses MUI 8px spacing unit)
 * - `m`, `mt`, `mb`, `ml`, `mr`, `mx`, `my`, `marginX`, `marginY` - margin shortcuts
 * - `p`, `pt`, `pb`, `pl`, `pr`, `px`, `py`, `paddingX`, `paddingY` - padding shortcuts
 * - `gap`, `rowGap`, `columnGap` - gap properties
 *
 * ### Sizing (numeric values use MUI spacing, ≤1 treated as percentage)
 * - `width`, `height`, `minWidth`, `minHeight`, `maxWidth`, `maxHeight`
 *
 * ### Position (numeric values use MUI 8px spacing unit)
 * - `top`, `right`, `bottom`, `left`
 *
 * ### Colors (supports theme palette paths like "primary.main")
 * - `bgcolor`, `backgroundColor`, `color`, `borderColor`
 *
 * ### Layout (common values mapped to Tailwind classes)
 * - `display` - flex, grid, none, block, etc. → Tailwind display classes
 * - `flex` - 1, auto, initial, none → flex-1, flex-auto, etc.
 * - `overflow` - auto, hidden, scroll, visible → overflow-* classes
 *
 * ### Other
 * - `border` - numeric values map to Tailwind border classes
 * - `borderRadius` - numeric values map to Tailwind rounded classes
 * - `boxShadow` - numeric values map to Tailwind shadow classes
 * - `fontWeight` - string names like 'bold', 'medium' map to Tailwind font-* classes
 * - `zIndex` - MUI tokens (modal, drawer, etc.) map to z-index classes
 * - `typography` - theme typography variants (h1-h6, body1, etc.)
 * - All standard CSS properties pass through as inline styles
 *
 * ### Theme Callback Support
 * - `sx={(theme) => ({ color: theme.palette.primary.main })}` is supported
 *
 * ## NOT Supported (will warn in development)
 *
 * - **Array syntax for responsive styles**: `display: ['none', 'block']`
 *   - Use Tailwind responsive prefixes instead: `className="hidden md:block"`
 *
 * - **Pseudo-selectors**: `':hover'`, `':focus'`, `':active'`, etc.
 *   - Use Tailwind variants instead: `className="hover:bg-blue-500"`
 *
 * - **Nested selectors**: `'& > div'`, `'& .child'`, etc.
 *   - Use Tailwind's child selectors or separate className logic
 *
 * - **Media queries**: `'@media (min-width: 600px)'`
 *   - Use Tailwind responsive prefixes: `className="md:flex"`
 *
 * - **CSS variables in keys**: Custom CSS variable definitions
 *
 * @example
 * ```tsx
 * import { processSxProps } from '@mint-lib/tw-utils';
 *
 * const MyComponent = (props) => {
 *   const { sx, className, style, ...rest } = props;
 *   const processed = processSxProps({ sx, className, style });
 *
 *   return (
 *     <div className={processed.className} style={processed.style} {...rest}>
 *       Hello World
 *     </div>
 *   );
 * };
 *
 * // Usage:
 * <MyComponent sx={{ p: 2, display: 'flex', bgcolor: 'primary.main' }} />
 * // Results in: style={{ padding: '16px', backgroundColor: 'var(--primary-main)' }}
 * //             className="flex"
 * ```
 *
 * @param incoming - Object containing sx, className, and style props
 * @returns Object with sx removed and className/style merged with processed values
 */
export function processSxProps<T extends SxIncoming>(
  incoming: T,
): Omit<T, 'sx'> {
  const { sx, ...rest } = incoming;
  if (sx) {
    if (Array.isArray(sx)) {
      warnUnsupportedFeature(
        'array syntax for sx',
        'Responsive array syntax like [value, null, value] for breakpoints is not supported. Use Tailwind responsive prefixes instead (e.g., md:flex)',
      );
      return {
        ...rest,
      };
    }
    if (typeof sx === 'function') {
      const result = sx(virtualTheme);
      if (result) {
        const { className, style } = applySx(result);
        return {
          ...rest,
          className: cn(rest.className, className),
          style: {
            ...rest.style,
            ...style,
          },
        };
      }
      return {
        ...rest,
      };
    }
    // @ts-expect-error We are sure that sx is a SystemStyleObject
    const { className, style } = applySx(sx);
    return {
      ...rest,
      className: cn(rest.className, className),
      style: {
        ...rest.style,
        ...style,
      },
    };
  }
  return rest;
}
